import React from 'react';
import { Modal, Descriptions, Tag, Space, Tooltip, message } from 'antd';
import { StyledCard } from './styled';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { DASHBOARD_TYPE, isAppModule } from '@utils/constants';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ECO_AI_ALERT_NWR_METRICS } from '@src/components/ai-alerts/constants';
import { getAlertDiagnosticsData } from '@src/api/aiAlerts';
import { get } from 'lodash';
import { setDiagnosticsData } from '@src/store/aiAlerts/actions';
import {
  useGetAIAlertType,
  AI_ALERT_TYPE,
} from '@src/components/ai-alerts/hooks/useGetAIAlertType';
const { confirm } = Modal;

export const AlertDescription = ({ info, showImpactedUniques = true }) => {
  const alertType = useGetAIAlertType(info.metricName);

  return (
    <StyledCard
      title={<h4>AI Alert: {info.alertId}</h4>}
      extra={'status' in info ? <Tag color="blue">{info.alertEndedText}</Tag> : ''}
      className="alert-description-card"
    >
      <Descriptions size="small" layout="vertical" column={2}>
        <Descriptions.Item label="Metric">{info.metricDisplayName}</Descriptions.Item>

        {'peakValue' in info && (
          <Descriptions.Item
            label={alertType === AI_ALERT_TYPE.SPIKE ? 'Peak Value' : 'Minimum Value'}
          >
            {info.peakValue}
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Time issue started">{info.issueStartTime}</Descriptions.Item>
        <Descriptions.Item label={info.timeEndedText}>{info.issueEndTime}</Descriptions.Item>
        {showImpactedUniques && 'cumulativeImpactedUniques' in info && (
          <>
            <Descriptions.Item
              label={
                <Tooltip title={'Cumulative Impacted Unique Devices'} placement="topLeft">
                  {'CIUD'}
                  <InfoCircleOutlined style={{ fontSize: 12, verticalAlign: 'super' }} />
                </Tooltip>
              }
            >
              <Space>
                <div>Count: {info.cumulativeImpactedUniques}</div>
                <div>Percentage: {info.impactedPercentage}%</div>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Tooltip title={'Cumulative Total Unique Devices'}>
                  {'CTUD'}
                  <InfoCircleOutlined style={{ fontSize: 12, verticalAlign: 'super' }} />
                </Tooltip>
              }
            >
              {info.cumulativeTotalUniques}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Root Cause" span={2}>
          {info.rootCause}
        </Descriptions.Item>
      </Descriptions>
    </StyledCard>
  );
};

export const createAlertDashboard = (dispatch, alert, diagnosticsData) => {
  // defaulting alertType to video as CDN partner alerts have alertType as null
  const { alertId, rootCauseDisplayLabel, alertType } = alert;

  const dashboard = {
    'module:trends': {
      aiAlerts: {
        selectedAlertId: alertId,
        selectedAlertType: alertType ? alertType : 'VIDEO_ALERT',
      },
    },
    'module:aiAlerts': {
      selectedAiAlertDiagnostics: diagnosticsData,
    },
  };

  const rootCauseParam = rootCauseDisplayLabel.replaceAll('&&&', ' and ');

  dispatch(
    createTempDashboard(
      DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS,
      dashboard,
      `Alert: ${rootCauseParam}`,
      { newBrowserTab: true },
    ),
  );
};

export const confirmAlertDrilldown = (dispatch, data) => {
  const { info, aiAlert } = data;
  const { alertType, alertId } = aiAlert;

  confirm({
    title: `Drilldown to this alert?`,
    content: (
      <AlertDescription
        info={info}
        showImpactedUniques={
          isAppModule ? ECO_AI_ALERT_NWR_METRICS.includes(aiAlert.metricName) : true
        }
      />
    ),
    onOk: async () => {
      await getAlertDiagnosticsData({
        params: {
          alertType,
          alertId,
        },
      })
        .then((response) => {
          return get(response, ['data'], {});
        })
        .then((diagnosticsData) => {
          setDiagnosticsData(diagnosticsData);
          createAlertDashboard(dispatch, aiAlert, diagnosticsData);
        })
        .catch((e) =>
          message.error('There was an error fetching diagnostics data. Please try again later.'),
        );
    },
    onCancel() {
      // pass
    },
    width: 550,
    bodyStyle: {
      width: 500,
    },
  });
};
